<template>
	<div
		class="split-container"
		:class="{ 'split-container--horizontal': direction === 'horizontal' }"
		data-testid="vue-split"
	>
		<slot />
	</div>
</template>

<script type="text/javascript">
/* eslint-disable no-console */
import Split from "split.js";

export default {
	/**
	 * That's copied from https://github.com/rayehizoba/vue-splitjs due to migration from Vue2 to Vue3
	 *
	 * License:
	 * MIT License

	Copyright (c) 2017 Ramoon Studio

	Permission is hereby granted, free of charge, to any person obtaining a copy
	of this software and associated documentation files (the "Software"), to deal
	in the Software without restriction, including without limitation the rights
	to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
	copies of the Software, and to permit persons to whom the Software is
	furnished to do so, subject to the following conditions:

	The above copyright notice and this permission notice shall be included in all
	copies or substantial portions of the Software.

	THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
	IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
	FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
	AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
	LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
	OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
	SOFTWARE.
	*/
	name: "VueSplit",

	emits: ["onDrag", "onDragEnd", "onDragStart"],

	props: {
		elements: {
			// Array of target split element ids
			type: Array,
			required: true,
			validator(value) {
				// must be array of strings
				const isValid = value.every((i) => typeof i === "string");
				if (!isValid) {
					console.error(
						`VueSplitJs: Invalid elements - "${value}". Must be array of strings`
					);
				}
				return isValid;
			}
		},

		direction: {
			// Direction to split: horizontal or vertical.
			type: String,
			default: "horizontal",
			validator(value) {
				let allowedValues = ["horizontal", "vertical"];
				const isValid = allowedValues.includes(value);
				if (!isValid) {
					console.error(
						`VueSplitJs: Invalid direction - "${value}". Possible values are "horizontal" or "vertical"`
					);
				}
				return isValid;
			}
		},

		sizes: {
			// Initial sizes of each element in percents.
			type: Array,
			default: null,
			validator(value) {
				// must be array of numbers
				const isValid = value.every((i) => typeof i === "number");
				if (!isValid) {
					console.error(
						`VueSplitJs: Invalid sizes - "${value}". Must be array of numbers`
					);
				}
				return isValid;
			}
		},

		minSize: {
			// Minimum size of each element in pixels.
			type: [Number, Array],
			default: 100
		},

		gutterSize: {
			// Gutter size in pixels.
			type: Number,
			default: 10
		},

		snapOffset: {
			// Snap to minimum size offset.
			type: Number,
			default: 30
		},

		cursor: {
			// Cursor to display while dragging.
			type: String,
			default: "grabbing"
		}
	},

	data() {
		return {
			split: null
		};
	},

	mounted() {
		this.split = Split(this.elements, {
			direction: this.direction,
			sizes: this.sizes,
			minSize: this.minSize,
			gutterSize: this.gutterSize,
			snapOffset: this.snapOffset,
			cursor: this.cursor,
			onDrag: this.onDrag,
			onDragStart: this.onDragStart,
			onDragEnd: this.onDragEnd
		});
	},

	methods: {
		onDrag() {
			this.$emit("onDrag", {
				sizes: this.split.getSizes()
			});
		},

		onDragEnd() {
			this.$emit("onDragEnd", {
				sizes: this.split.getSizes()
			});
		},

		onDragStart() {
			this.$emit("onDragStart", {
				sizes: this.split.getSizes()
			});
		}
	}
};
</script>

<style lang="scss">
$color-screen-splitter-background: #eee;

.split-container {
	height: 100%;
}

.split-container > * {
	position: relative;
	overflow-y: hidden;
}

.split-container--horizontal {
	display: flex;
}

.gutter {
	background-color: $color-screen-splitter-background;
	background-repeat: no-repeat;
	background-position: 50%;
}

.gutter.gutter-vertical {
	background-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB4AAAAFAQMAAABo7865AAAABlBMVEVHcEzMzMzyAv2sAAAAAXRSTlMAQObYZgAAABBJREFUeF5jOAMEEAIEEFwAn3kMwcB6I2AAAAAASUVORK5CYII=");
	cursor: ns-resize;
}

.gutter.gutter-horizontal {
	float: left;
	cursor: ew-resize;
	background-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAUAAAAeCAYAAADkftS9AAAAIklEQVQoU2M4c+bMfxAGAgYYmwGrIIiDjrELjpo5aiZeMwF+yNnOs5KSvgAAAABJRU5ErkJggg==");
}

.split {
	-webkit-box-sizing: border-box;
	-moz-box-sizing: border-box;
	box-sizing: border-box;
	overflow-y: auto;
	overflow-x: hidden;
}
</style>
